/* eslint-disable react/no-danger */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from '../../../i18n';

import * as fromProviders from '../../../entities/providers/selectors';
import * as fromRates from '../../../entities/rates/selectors';
import * as fromExt from '../../../entities/rates/selectors/extensions.selectors';
import { actions as ratesActs, ExtensionTypes, Extension } from '../../../entities/rates';

import { ExtensionElement, ExtensionName, ExtensionHint, ExtensionRight, ExtensionDetails } from './extensions.styled';
import { ExtensionCheckbox } from './checkbox';

import { priceCurrencyToSign } from '../../../helpers/currency';

function normalizePrice(price: number | null, percent: number | null) {
  if (price) {
    return priceCurrencyToSign(price.toString());
  }

  return `${percent}%`;
}

const SERVICE: ExtensionTypes = 'insurance';

export const Insurance: React.FC<{ insurance: Extension }> = ({ insurance: { fixed_price, percent_price } }) => {
  const [t] = useTranslation('rates');

  const [enabled, setEnabled] = useState(false);

  const dispatch = useDispatch();

  function toggle(status: boolean) {
    setEnabled(status);

    dispatch(status ? ratesActs.checkExt({ ext: SERVICE }) : ratesActs.unCheckExt({ ext: SERVICE }));
  }

  const insurancePrice = useMemo(() => normalizePrice(fixed_price, percent_price), [percent_price, fixed_price]);

  const parcelPrice = useSelector(fromProviders.getParcelPriceWithDisc);

  const deliveryPrice = useMemo(() => priceCurrencyToSign(parcelPrice), [parcelPrice]);

  const fullExtPrice = useSelector(fromExt.getExtFullPrice(SERVICE));
  const isHigherPrice = useSelector(fromExt.getIsHigherPrice(SERVICE));
  const extPrice = useSelector(fromExt.getExtPrice(SERVICE));
  const maxCompensation = useSelector(fromExt.getExtMaxCompensations(SERVICE));
  const declarationPrice = useSelector(fromRates.getDeclarationFullPrice);

  // [HOOK] trigger price update
  useEffect(() => {
    dispatch(ratesActs.checkExt({ ext: SERVICE, emit: false }));
  }, [declarationPrice]);

  // @ts-ignore
  return (
    <>
      <ExtensionElement>
        <ExtensionName>{t('rates.extension.insurance.without.title')}</ExtensionName>
        <ExtensionRight>
          <ExtensionCheckbox checked={!enabled} onChange={() => toggle(false)} />
        </ExtensionRight>
        <ExtensionHint italic noSpace>
          {t('rates.extension.insurance.without.subtitle')} - {deliveryPrice}
        </ExtensionHint>
      </ExtensionElement>

      <ExtensionElement>
        <ExtensionName>{t('rates.extension.insurance.with.title')}</ExtensionName>
        <ExtensionRight>
          {enabled && <b>+{insurancePrice}</b>}
          {!enabled && <ExtensionCheckbox checked={enabled} onChange={() => toggle(true)} />}
        </ExtensionRight>

        {enabled && (
          <>
            <ExtensionDetails>
              <p>{t('rates.extension.insurance.with.in_case')}:</p>
              <ul>
                <li>
                  {t('rates.extension.insurance.with.delivery')} - {deliveryPrice}
                </li>
                <li>
                  {t('rates.extension.insurance.with.compensation')}
                  {isHigherPrice ? (
                    <span dangerouslySetInnerHTML={{ __html: t('rates.extension.insurance.with.compensationMax') }} />
                  ) : (
                    ' -'
                  )}{' '}
                  {maxCompensation}
                </li>
              </ul>
              <ExtensionHint
                italic
                noSpace
                dangerouslySetInnerHTML={{ __html: t('rates.extension.insurance.with.details') }}
              />
              <hr />
            </ExtensionDetails>

            <ExtensionName>
              {extPrice && `+${priceCurrencyToSign(extPrice)}`}
              <ExtensionHint>
                {t('rates.extension.insurance.with.total')}: {priceCurrencyToSign(fullExtPrice)}
              </ExtensionHint>
            </ExtensionName>
            <ExtensionRight>
              <ExtensionCheckbox checked={enabled} onChange={() => toggle(true)} />
            </ExtensionRight>
          </>
        )}
      </ExtensionElement>
    </>
  );
};
